// Colic Coverage right-column aside: horses on this account, related
// subscription, coverage impact warning, and program information links.
//
// TODO(runtime): hydrate from
//   - services/horse-timeline (horses on account)
//   - dist/account-workflows.subscriptions (related subscription)
//   - services/customer-coverage (coverage impact rules)
import type { ReactNode } from 'react';
import { Icon } from '../../_shared/IconSprite.js';
import type { CoverageHorse } from '../mockData.js';

export interface CoverageSidebarProps {
  horses: CoverageHorse[];
  activeHorseId: string;
  onSelectHorse?: (horseId: string) => void;
  onReviewSubscription?: () => void;
  onReviewImpact?: () => void;
  onProgramLink?: (key: 'terms' | 'acceptance') => void;
}

export function CoverageSidebar({
  horses,
  activeHorseId,
  onSelectHorse,
  onReviewSubscription,
  onReviewImpact,
  onProgramLink,
}: CoverageSidebarProps): ReactNode {
  const active = horses.find((h) => h.id === activeHorseId) ?? horses[0];
  if (!active) return null;

  return (
    <aside className="coverage-right">
      <section className="card horses-on-account">
        <div className="section-heading">
          <h3>Horses on this account</h3>
          <span>
            {horses.length} horse{horses.length === 1 ? '' : 's'}
          </span>
        </div>
        {horses.map((horse) => (
          <button
            key={horse.id}
            className="horse-select"
            type="button"
            onClick={() => onSelectHorse?.(horse.id)}
          >
            <span className="horse-initial">{horse.initial}</span>
            <div>
              <strong>{horse.name}</strong>
              <p>Application {horse.applicationStatus.toLowerCase()}</p>
            </div>
            <Icon id="arrow" />
          </button>
        ))}
        <p className="side-note">Each horse has its own enrollment and coverage status.</p>
      </section>

      <section className="card related-subscription-card">
        <div className="section-heading">
          <h3>Related subscription</h3>
        </div>
        <div className="related-sub">
          <span className="product-square">{active.relatedProduct.square}</span>
          <div>
            <strong>{active.relatedProduct.name}</strong>
            <p>{active.relatedProduct.note}</p>
          </div>
          <span className="teal-status">Recorded</span>
        </div>
        <div className="supply-status">
          <strong>Qualifying supply not verified</strong>
          <p>
            Confirm the applicable program requirements before representing this plan as qualifying.
          </p>
        </div>
        <button className="card-link" type="button" onClick={onReviewSubscription}>
          Review subscription
          <Icon id="arrow" />
        </button>
      </section>

      <section className="coverage-impact">
        <Icon id="refresh" />
        <div>
          <strong>Before changing a subscription</strong>
          <p>
            Check whether skipping, pausing, cancelling or changing products affects this horse's
            coverage.
          </p>
          <button className="text-btn" type="button" onClick={onReviewImpact}>
            Review program impact
          </button>
        </div>
      </section>

      <section className="card program-info-card">
        <div className="section-heading">
          <h3>Program information</h3>
        </div>
        <div className="program-links">
          <button type="button" onClick={() => onProgramLink?.('terms')}>
            Current terms &amp; eligibility
            <Icon id="arrow" />
          </button>
          <button type="button" onClick={() => onProgramLink?.('acceptance')}>
            Written acceptance record
            <Icon id="arrow" />
          </button>
        </div>
        <p className="side-note">
          No coverage amount or benefit is shown until current terms are verified.
        </p>
      </section>
    </aside>
  );
}
